import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useWebSocket } from '../context/WebSocketContext';
import axios from 'axios';
import { MessageCircle, Users, Search, Settings, Plus, QrCode, X, UserPlus, MoreVertical, User, LogOut, Moon, Sun, Shield, Check, ArrowLeft, Pin, Star, Heart, CheckSquare, Trash2, CheckCheck, Archive, ChevronDown, BellOff, Ban } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import StoriesBar from '../components/StoriesBar';
import BottomNav from '../components/BottomNav';
import SkyHeader, { getSkyDividerColor } from '../components/SkyHeader';
import { resolveAvatarUrl } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatList = ({ isEmbedded = false, selectedChatId = null, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const { addMessageHandler } = useWebSocket();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalSearchResults, setModalSearchResults] = useState([]);
  const [modalSearching, setModalSearching] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Group chat states
  const [chatMode, setChatMode] = useState('direct'); // 'direct' or 'group'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  // Filter tabs
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'unread', 'favorites', 'groups'
  // Multi-chat selection
  const [chatSelectMode, setChatSelectMode] = useState(false);
  const [selectedChats, setSelectedChats] = useState([]);
  const [profileMenuPos, setProfileMenuPos] = useState(null);
  const [moreMenuPos, setMoreMenuPos] = useState(null);
  const [showStoriesModal, setShowStoriesModal] = useState(false);
  const [chatContextMenu, setChatContextMenu] = useState(null); // { chatId, pos: {top, left} }
  const chatTouchStartX = useRef(0);
  const chatTouchStartY = useRef(0);
  const [swipedChat, setSwipedChat] = useState(null); // { id, direction }

  useEffect(() => {
    fetchChats();
  }, [refreshKey]);

  // Listen for WebSocket messages to refresh chat list
  useEffect(() => {
    const cleanup = addMessageHandler((data) => {
      if (data.type === 'new_message' && data.message) {
        // Update only display content in-place
        setChats(prev => {
          const chatId = data.message.chat_id || data.chat_id;
          const idx = prev.findIndex(c => c.id === chatId);
          if (idx !== -1) {
            const updated = [...prev];
            const chat = updated[idx];
            // Only increment unread if we're not currently viewing this chat
            const isCurrentChat = window.location.pathname.includes(chatId);
            updated[idx] = {
              ...chat,
              last_message: data.message.content,
              last_message_time: data.message.timestamp,
              unread_count: isCurrentChat ? chat.unread_count : (chat.unread_count || 0) + 1
            };
            return updated;
          }
          return prev;
        });
        // Don't auto-refresh - let user see the update in place
        // Polling will handle sort order updates
      } else if (['task_completed', 'task_overdue', 'reaction_update'].includes(data.type)) {
        fetchChats();
      }
    });
    return cleanup;
  }, [addMessageHandler]);

  // Periodic polling fallback for missed WebSocket events
  useEffect(() => {
    const interval = setInterval(() => {
      fetchChats();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Refresh chat list when app comes to foreground
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchChats();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu || showMoreMenu) {
        const isClickInsideMenu = event.target.closest('[data-menu]');
        if (!isClickInsideMenu) {
          setShowProfileMenu(false);
          setShowMoreMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu, showMoreMenu]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (chatSelectMode) {
          setChatSelectMode(false);
          setSelectedChats([]);
        } else {
          setShowProfileMenu(false);
          setShowMoreMenu(false);
          setShowNewChat(false);
          setChatContextMenu(null);
        }
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [chatSelectMode]);

  const fetchChats = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/chats`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      setChats(response.data);
    } catch (error) {
      console.error('Failed to fetch chats:', error);
      // Auto-retry up to 3 times before showing error
      if (retryCount < 3) {
        setTimeout(() => fetchChats(retryCount + 1), 1500 * (retryCount + 1));
        return; // Don't set loading=false yet
      }
      if (chats.length === 0) {
        toast.error('Failed to load chats. Pull down to retry.');
      }
    } finally {
      if (retryCount === 0 || retryCount >= 3) setLoading(false);
    }
  };

  // Filter chats based on active filter
  const getFilteredChats = () => {
    let filtered = chats;
    
    // First, filter out archived chats from normal views
    if (activeFilter !== 'archived') {
      filtered = filtered.filter(chat => !chat.archived_by?.includes(user?.id));
    }
    
    switch (activeFilter) {
      case 'unread':
        filtered = filtered.filter(chat => chat.unread_count > 0);
        break;
      case 'favorites':
        filtered = filtered.filter(chat => chat.is_favorite);
        break;
      case 'groups':
        filtered = filtered.filter(chat => chat.chat_type === 'group');
        break;
      case 'archived':
        filtered = chats.filter(chat => chat.archived_by?.includes(user?.id));
        break;
      default:
        // 'all' - no filtering (archived already excluded)
        break;
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(chat => {
        const chatName = chat.chat_type === 'direct' 
          ? chat.other_user?.display_name || chat.other_user?.username
          : chat.group_name || chat.name;
        return chatName?.toLowerCase().includes(query);
      });
    }
    
    return filtered;
  };

  // Sort chats: pinned first, then by last_message_time (memoized to prevent re-render jitter)
  const sortedFilteredChats = useMemo(() => {
    const filtered = getFilteredChats();
    // Use spread to avoid mutating the original array, then stable sort
    return [...filtered].sort((a, b) => {
      // Pinned chats always first
      const aPinned = a.is_pinned ? 1 : 0;
      const bPinned = b.is_pinned ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      // Within same pin status, sort by last_message_time
      const aTime = a.last_message_time || a.last_message_at || a.created_at || '';
      const bTime = b.last_message_time || b.last_message_at || b.created_at || '';
      return bTime.localeCompare(aTime);
    });
  }, [chats, searchQuery, activeFilter, user?.id]);

  // Pin/Unpin chat
  const handlePinChat = async (chatId, isPinned, e) => {
    e.stopPropagation();
    try {
      if (isPinned) {
        await axios.delete(`${API}/chats/${chatId}/pin`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Chat unpinned');
      } else {
        await axios.post(`${API}/chats/${chatId}/pin`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Chat pinned');
      }
      fetchChats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pin');
    }
  };

  // Favorite/Unfavorite chat
  const handleFavoriteChat = async (chatId, isFavorite, e) => {
    e.stopPropagation();
    try {
      if (isFavorite) {
        await axios.delete(`${API}/chats/${chatId}/favorite`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/chats/${chatId}/favorite`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Added to favorites');
      }
      fetchChats();
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // Archive/Unarchive chat
  const handleArchiveChat = async (chatId, isArchived, e) => {
    e.stopPropagation();
    try {
      if (isArchived) {
        await axios.delete(`${API}/chats/${chatId}/archive`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Chat unarchived');
      } else {
        await axios.post(`${API}/chats/${chatId}/archive`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Chat archived');
      }
      fetchChats();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Toggle chat selection
  const toggleChatSelect = (chatId, e) => {
    e.stopPropagation();
    setSelectedChats(prev => 
      prev.includes(chatId) 
        ? prev.filter(id => id !== chatId)
        : [...prev, chatId]
    );
  };

  // Mark selected chats as read
  const handleBulkMarkAsRead = async () => {
    if (selectedChats.length === 0) return;
    
    try {
      // Mark all messages in each selected chat as read
      await Promise.all(selectedChats.map(chatId => 
        axios.post(`${API}/messages/bulk-read?chat_id=${chatId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));
      toast.success(`${selectedChats.length} chat(s) marked as read`);
      setSelectedChats([]);
      setChatSelectMode(false);
      fetchChats();
    } catch (error) {
      toast.error('Failed to mark chats as read');
    }
  };

  // Delete selected chats
  const handleBulkDeleteChats = async () => {
    if (selectedChats.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedChats.length} chat(s)? This cannot be undone.`)) return;
    
    try {
      // Delete each selected chat
      await Promise.all(selectedChats.map(chatId => 
        axios.delete(`${API}/chats/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));
      toast.success(`${selectedChats.length} chat(s) deleted`);
      setSelectedChats([]);
      setChatSelectMode(false);
      fetchChats();
    } catch (error) {
      toast.error('Failed to delete chats');
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Use global search endpoint for comprehensive results
      const response = await axios.get(`${API}/search`, {
        params: { query },
        headers: { Authorization: `Bearer ${token}` }
      });
      // Combine users and message results for display
      const combinedResults = {
        users: response.data.users || [],
        chats: response.data.chats || [],
        messages: response.data.messages || []
      };
      setSearchResults(combinedResults);
    } catch (error) {
      // Fallback to user search if global search fails
      try {
        const response = await axios.get(`${API}/users/search`, {
          params: { query },
          headers: { Authorization: `Bearer ${token}` }
        });
        setSearchResults({ users: response.data, chats: [], messages: [] });
      } catch (e) {
        console.error('Search failed:', e);
      }
    } finally {
      setSearching(false);
    }
  };

  const searchUsersInModal = async (query) => {
    if (!query.trim()) {
      setModalSearchResults([]);
      return;
    }

    setModalSearching(true);
    try {
      const response = await axios.get(`${API}/users/search`, {
        params: { query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setModalSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setModalSearching(false);
    }
  };

  const createChat = async (userId) => {
    try {
      const response = await axios.post(
        `${API}/chats`,
        {
          chat_type: 'direct',
          participant_ids: [userId]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create chat:', error);
      toast.error('Failed to create chat');
    }
  };

  const createGroupChat = async () => {
    if (selectedUsers.length < 2) {
      toast.error('Please select at least 2 members for a group');
      return;
    }
    if (!groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    setCreatingGroup(true);
    try {
      const response = await axios.post(
        `${API}/chats`,
        {
          chat_type: 'group',
          participant_ids: selectedUsers.map(u => u.id),
          group_name: groupName.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Group created!');
      navigate(`/chat/${response.data.id}`);
      resetModal();
    } catch (error) {
      console.error('Failed to create group:', error);
      toast.error('Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const createBroadcastList = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least 1 member');
      return;
    }
    if (!groupName.trim()) {
      toast.error('Please enter a broadcast list name');
      return;
    }
    setCreatingGroup(true);
    try {
      await axios.post(`${API}/broadcast-lists`, {
        name: groupName.trim(),
        member_ids: selectedUsers.map(u => u.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Broadcast list "${groupName.trim()}" created with ${selectedUsers.length} members!`);
      resetModal();
    } catch (error) {
      toast.error('Failed to create broadcast list');
    } finally {
      setCreatingGroup(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const resetModal = () => {
    setShowNewChat(false);
    setModalSearchQuery('');
    setModalSearchResults([]);
    setChatMode('direct');
    setSelectedUsers([]);
    setGroupName('');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return format(date, 'HH:mm');
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return format(date, 'dd/MM/yyyy');
    } catch {
      return '';
    }
  };

  return (
    <div className={`${isEmbedded ? 'h-full relative' : 'min-h-screen pb-14'} flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-950' : 'bg-background-DEFAULT'
    }`} data-testid="chat-list-page">
      {/* Header */}
      <div className={`${isEmbedded ? '' : 'sticky top-0'} z-20`}>
      <SkyHeader>
        <div>
          <div className={`${isEmbedded ? 'p-4' : 'chat-container p-4 safe-area-top'}`}>
            <div className="flex items-center justify-between mb-4">
              <h1 className={`font-heading ${isEmbedded ? 'text-xl' : 'text-2xl'} text-white drop-shadow-sm`}>ChatBuzzz</h1>
            <div className="flex gap-3 items-center">
              {/* New Chat Button for embedded mode - always visible in header */}
              {isEmbedded && (
                <button
                  data-testid="new-chat-btn-header"
                  onClick={() => setShowNewChat(true)}
                  className="w-9 h-9 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center"
                  title="New Chat"
                >
                  <Plus size={18} />
                </button>
              )}
              {/* Profile Icon with Dropdown */}
              <div className="relative" data-menu>
                <button
                  data-testid="profile-btn"
                  onClick={(e) => {
                    setShowMoreMenu(false);
                    if (!showProfileMenu) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setProfileMenuPos({
                        top: rect.bottom + 4,
                        left: Math.max(8, Math.min(rect.left, window.innerWidth - 272))
                      });
                    }
                    setShowProfileMenu(!showProfileMenu);
                  }}
                  className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors hover:bg-white/20 bg-white/10`}
                >
                  <User size={20} className="text-white" />
                </button>
                
                {/* Profile Dropdown - via portal for overflow safety */}
                {showProfileMenu && profileMenuPos && createPortal(
                  <div 
                    data-menu
                    className={`fixed w-64 rounded-2xl shadow-lg border z-[9999] ${
                      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-border-DEFAULT'
                    }`}
                    style={{ top: profileMenuPos.top, left: profileMenuPos.left }}
                  >
                    <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-border-DEFAULT'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                          isDarkMode ? 'bg-gray-700' : 'bg-secondary-DEFAULT'
                        }`}>
                          <User size={24} className={isDarkMode ? 'text-gray-300' : 'text-text-primary'} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{user?.display_name}</p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-tertiary'}`}>@{user?.username}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          setShowStoriesModal(true);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-secondary-DEFAULT text-text-primary'
                        }`}
                      >
                        <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center"><Plus size={12} className="text-[#D4AF37]" /></div>
                        <span>My Status</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          navigate('/settings/accounts');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-secondary-DEFAULT text-text-primary'
                        }`}
                      >
                        <UserPlus size={18} />
                        <span>Add / Switch Account</span>
                      </button>
                    </div>
                  </div>,
                  document.body
                )}
              </div>

              {/* 3-Dot Menu */}
              <div className="relative" data-menu>
                <button
                  data-testid="more-menu-btn"
                  onClick={(e) => {
                    setShowProfileMenu(false);
                    if (!showMoreMenu) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMoreMenuPos({
                        top: rect.bottom + 4,
                        left: Math.max(8, Math.min(rect.left, window.innerWidth - 232))
                      });
                    }
                    setShowMoreMenu(!showMoreMenu);
                  }}
                  className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors hover:bg-white/20 text-white`}
                >
                  <MoreVertical size={20} />
                </button>

                {/* More Menu Dropdown - via portal for overflow safety */}
                {showMoreMenu && moreMenuPos && createPortal(
                  <div 
                    data-menu
                    className={`fixed w-56 rounded-2xl shadow-lg border z-[9999] ${
                      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-border-DEFAULT'
                    }`}
                    style={{ top: moreMenuPos.top, left: moreMenuPos.left }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          navigate('/devices');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-secondary-DEFAULT text-text-primary'
                        }`}
                      >
                        <QrCode size={18} />
                        <span>Linked Devices</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          navigate('/settings');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-secondary-DEFAULT text-text-primary'
                        }`}
                      >
                        <Settings size={18} />
                        <span>Settings</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          setChatSelectMode(true);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-secondary-DEFAULT text-text-primary'
                        }`}
                        data-testid="select-chats-btn"
                      >
                        <CheckSquare size={18} />
                        <span>Select chats</span>
                      </button>
                      <div className={`my-1 mx-3 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        data-testid="quick-logout-btn"
                      >
                        <LogOut size={18} />
                        <span>Log Out</span>
                      </button>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
            <input
              data-testid="search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchUsers(e.target.value);
              }}
              className={`w-full h-10 rounded-full pl-12 pr-4 text-sm border-none focus:ring-1 focus:ring-[#D4AF37] transition-colors bg-white/20 text-white placeholder:text-white/50 backdrop-blur-sm`}
              placeholder="Search users or invite codes..."
            />
          </div>
          
          {/* Filter Tabs */}
          <div className={`flex gap-2 mt-3 overflow-x-auto scrollbar-hide ${isEmbedded ? '' : 'px-2'}`}>
            {[
              { id: 'all', label: 'All' },
              { id: 'unread', label: 'Unread' },
              { id: 'favorites', label: 'Favorites', icon: Heart },
              { id: 'groups', label: 'Groups', icon: Users }
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  activeFilter === filter.id
                    ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-sm'
                    : 'bg-white/15 text-white/80 hover:bg-white/25'
                }`}
                data-testid={`filter-${filter.id}`}
              >
                {filter.icon && <filter.icon size={14} />}
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        </div>
      </SkyHeader>
      </div>

      {/* Archive Row (WhatsApp style - shown at top of chats when there are archived chats) */}
      {(() => {
        const archivedCount = chats.filter(c => c.archived_by?.includes(user?.id)).length;
        return archivedCount > 0 && activeFilter !== 'archived' ? (
          <button
            onClick={() => setActiveFilter('archived')}
            className={`w-full flex items-center gap-4 px-6 py-3 border-b ${isDarkMode ? 'border-gray-800 hover:bg-gray-900' : 'border-gray-100 hover:bg-gray-50'}`}
            data-testid="archived-chats-row"
          >
            <Archive size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Archived</span>
            <span className={`text-xs ml-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{archivedCount}</span>
          </button>
        ) : null;
      })()}

      {/* Selection Mode Bar */}
      {chatSelectMode && (
        <div className={`sticky z-10 border-b ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-border-DEFAULT'
        }`}>
          <div className={`${isEmbedded ? 'p-3' : 'chat-container p-3'} flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setChatSelectMode(false);
                  setSelectedChats([]);
                }}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
              <span className={isDarkMode ? 'text-white' : 'text-text-primary'}>
                {selectedChats.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkMarkAsRead}
                disabled={selectedChats.length === 0}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg ${
                  isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } disabled:opacity-50`}
                data-testid="bulk-mark-read-btn"
              >
                <CheckCheck size={16} />
                Mark as read
              </button>
              <button
                onClick={handleBulkDeleteChats}
                disabled={selectedChats.length === 0}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                data-testid="bulk-delete-chats-btn"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Results or Chat List */}
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className={`${isEmbedded ? '' : 'chat-container'}`}>
          {searchQuery ? (
            <div className="py-4" data-testid="search-results">
              <h2 className={`text-sm font-medium px-6 mb-3 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>SEARCH RESULTS</h2>
              {searching ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>Searching...</div>
              ) : ((!searchResults.users?.length && !searchResults.messages?.length && !Array.isArray(searchResults)) || (Array.isArray(searchResults) && searchResults.length === 0)) ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>No results found</div>
              ) : (
                <div className="space-y-4">
                  {/* Users Section */}
                  {searchResults.users?.length > 0 && (
                    <div>
                      <h3 className={`px-6 text-xs font-medium uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        People
                      </h3>
                      {searchResults.users.map((foundUser) => (
                        <div
                          key={foundUser.id}
                          data-testid={`user-result-${foundUser.id}`}
                          onClick={() => createChat(foundUser.id)}
                          className={`flex items-center gap-4 p-4 mx-4 rounded-2xl transition-all duration-200 cursor-pointer ${
                            isDarkMode 
                              ? 'hover:bg-gray-800/70' 
                              : 'hover:bg-[#F0F0F0]'
                          }`}
                        >
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                            {(foundUser.display_name || foundUser.username || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{foundUser.display_name}</h3>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>@{foundUser.username}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Messages Section */}
                  {searchResults.messages?.length > 0 && (
                    <div>
                      <h3 className={`px-6 text-xs font-medium uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Messages
                      </h3>
                      {searchResults.messages.slice(0, 10).map((msg) => (
                        <div
                          key={msg.id}
                          onClick={() => navigate(`/chat/${msg.chat_id}`)}
                          className={`flex items-start gap-4 p-4 mx-4 rounded-2xl transition-all duration-200 cursor-pointer ${
                            isDarkMode 
                              ? 'hover:bg-gray-800/70' 
                              : 'hover:bg-[#F0F0F0]'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37]/50 to-[#C5A059]/50 flex items-center justify-center">
                            <MessageCircle size={16} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                              {msg.content}
                            </p>
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}`}>
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Fallback for legacy array format */}
                  {Array.isArray(searchResults) && searchResults.map((foundUser) => (
                    <div
                      key={foundUser.id}
                      data-testid={`user-result-${foundUser.id}`}
                      onClick={() => createChat(foundUser.id)}
                      className={`flex items-center gap-4 p-4 mx-4 rounded-2xl transition-all duration-200 cursor-pointer ${
                        isDarkMode 
                          ? 'hover:bg-gray-800/70' 
                          : 'hover:bg-[#F0F0F0]'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                        {(foundUser.display_name || foundUser.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{foundUser.display_name}</h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>@{foundUser.username}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="py-2" data-testid="chats-list">
              {loading ? (
                <div className="space-y-1" data-testid="chat-list-skeleton">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className={`flex items-center gap-3 py-2 px-3 animate-pulse`}>
                      <div className={`w-11 h-11 rounded-full flex-shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className={`h-4 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} style={{ width: `${60 + (i % 3) * 20}px` }} />
                          <div className={`h-3 w-10 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
                        </div>
                        <div className={`h-3 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} style={{ width: `${120 + (i % 4) * 30}px` }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : chats.length === 0 ? (
                <div className="text-center py-12">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    isDarkMode ? 'bg-gray-800' : 'bg-secondary-DEFAULT'
                  }`}>
                    <MessageCircle size={32} className={isDarkMode ? 'text-gray-500' : 'text-text-tertiary'} />
                  </div>
                  <h3 className={`font-heading text-xl mb-2 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>No chats yet</h3>
                  <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>Search for users to start a conversation</p>
                </div>
              ) : sortedFilteredChats.length === 0 ? (
                <div className="text-center py-12">
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                    {activeFilter === 'unread' ? 'No unread messages' : 
                     activeFilter === 'favorites' ? 'No favorite chats' :
                     activeFilter === 'groups' ? 'No group chats' :
                     activeFilter === 'archived' ? 'No archived chats' : 'No chats found'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {sortedFilteredChats.map((chat) => {
                    const otherUser = chat.other_user;
                    const displayName = chat.chat_type === 'direct' 
                      ? (otherUser?.display_name || 'Unknown')
                      : (chat.group_name || chat.name || 'Chat');
                    const unreadCount = chat.unread_count || 0;
                    const isSelected = selectedChatId === chat.id;
                    const isSystemChat = ['tasks', 'reminders', 'ai_buddy'].includes(chat.system_chat_type);
                    
                    return (
                      <React.Fragment key={chat.id}>
                      <div
                        data-testid={`chat-item-${chat.id}`}
                        onClick={() => {
                          if (chatSelectMode) {
                            toggleChatSelect(chat.id, { stopPropagation: () => {} });
                          } else {
                            navigate(`/chat/${chat.id}`);
                          }
                        }}
                        onTouchStart={(e) => {
                          chatTouchStartX.current = e.touches[0].clientX;
                          chatTouchStartY.current = e.touches[0].clientY;
                        }}
                        onTouchMove={(e) => {
                          const diffX = e.touches[0].clientX - chatTouchStartX.current;
                          const diffY = Math.abs(e.touches[0].clientY - chatTouchStartY.current);
                          if (diffY > 20) { setSwipedChat(null); return; }
                          if (diffX < -60 && !isSystemChat) setSwipedChat({ id: chat.id, direction: 'left' });
                          else if (diffX > 60) setSwipedChat({ id: chat.id, direction: 'right' });
                          else setSwipedChat(null);
                        }}
                        onTouchEnd={() => {
                          if (swipedChat?.id === chat.id) {
                            if (swipedChat.direction === 'left') {
                              handleArchiveChat(chat.id, chat.archived_by?.includes(user?.id), { stopPropagation: () => {} });
                            } else if (swipedChat.direction === 'right') {
                              handlePinChat(chat.id, chat.is_pinned, { stopPropagation: () => {} });
                            }
                            setSwipedChat(null);
                          }
                        }}
                        className={`group relative flex items-center gap-3 py-2 px-3 transition-all duration-200 cursor-pointer ${
                          swipedChat?.id === chat.id
                            ? swipedChat.direction === 'left' ? '-translate-x-16' : 'translate-x-16'
                            : ''
                        } ${
                          chatSelectMode && selectedChats.includes(chat.id)
                            ? isDarkMode 
                              ? 'bg-[#D4AF37]/20 border-[#D4AF37]/30' 
                              : 'bg-[#D4AF37]/10 border-[#D4AF37]/20'
                            : isSelected
                              ? isDarkMode 
                                ? 'bg-[#D4AF37]/20 border-[#D4AF37]/30' 
                                : 'bg-[#D4AF37]/10 border-[#D4AF37]/20'
                              : isDarkMode 
                                ? 'hover:bg-gray-800/70 active:bg-gray-700 border-gray-800' 
                                : 'hover:bg-[#F0F0F0] active:bg-[#E5E5E5] border-[#F5F5F7]'
                        }`}
                      >
                        {/* Selection checkbox */}
                        {chatSelectMode && (
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedChats.includes(chat.id)}
                              onChange={(e) => toggleChatSelect(chat.id, e)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-5 h-5 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                              data-testid={`chat-select-${chat.id}`}
                            />
                          </div>
                        )}
                        
                        {/* Favorite indicator */}
                        {!chatSelectMode && chat.is_favorite && (
                          <div className="absolute top-2 left-2">
                            <Heart size={12} className="text-red-500 fill-red-500" />
                          </div>
                        )}
                        
                        <div className="relative">
                          {chat.chat_type === 'direct' && otherUser?.avatar_url ? (
                            <img 
                              src={resolveAvatarUrl(otherUser.avatar_url)} 
                              alt={displayName}
                              className="w-11 h-11 rounded-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-medium ${
                            chat.system_chat_type === 'ai_buddy' 
                              ? 'bg-gradient-to-br from-purple-500 to-indigo-600'
                              : chat.system_chat_type === 'tasks'
                                ? 'bg-gradient-to-br from-emerald-500 to-green-600'
                                : chat.system_chat_type === 'reminders'
                                  ? 'bg-gradient-to-br from-orange-500 to-amber-500'
                                  : 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]'
                          }`} style={chat.chat_type === 'direct' && otherUser?.avatar_url ? { display: 'none' } : {}}>
                            {chat.chat_type === 'group' ? (
                              <Users size={24} />
                            ) : chat.system_chat_type === 'ai_buddy' ? (
                              '🧠'
                            ) : chat.system_chat_type === 'tasks' ? (
                              '✅'
                            ) : (
                              (displayName || '?').charAt(0).toUpperCase()
                            )}
                          </div>
                          {chat.disappearing_messages > 0 && (
                            <div className={`absolute -bottom-0.5 -left-0.5 w-5 h-5 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1 min-w-0 flex-1">
                              <h3 className={`font-medium truncate ${
                                unreadCount > 0 
                                  ? (isDarkMode ? 'text-white font-semibold' : 'text-text-primary font-semibold') 
                                  : (isDarkMode ? 'text-white' : 'text-text-primary')
                              }`}>
                                {displayName}
                              </h3>
                              {chat.is_pinned && (
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center" data-testid={`pin-icon-${chat.id}`}>
                                  <Pin size={12} className={`${isDarkMode ? 'text-[#D4AF37]' : 'text-[#B8960C]'} rotate-45`} />
                                </div>
                              )}
                            </div>
                            {chat.last_message_time && (
                              <span className={`text-xs flex-shrink-0 ${
                                unreadCount > 0 
                                  ? 'text-[#D4AF37] font-medium' 
                                  : (isDarkMode ? 'text-gray-500' : 'text-text-tertiary')
                              }`}>
                                {formatTime(chat.last_message_time)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <p className={`text-sm truncate ${
                              unreadCount > 0 
                                ? (isDarkMode ? 'text-gray-200 font-medium' : 'text-text-primary font-medium') 
                                : (isDarkMode ? 'text-gray-400' : 'text-text-secondary')
                            }`}>
                              {chat.last_message || 'No messages yet'}
                            </p>
                            <div className="flex items-center gap-2 ml-2">
                              {/* Dropdown arrow - always visible on mobile, hover on desktop */}
                              {!chatSelectMode && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const menuHeight = 280;
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    const flipUp = spaceBelow < menuHeight;
                                    setChatContextMenu(chatContextMenu?.chatId === chat.id ? null : {
                                      chatId: chat.id,
                                      chatData: chat,
                                      isSystem: isSystemChat,
                                      pos: { 
                                        top: flipUp ? Math.max(8, rect.top - menuHeight) : rect.bottom + 4, 
                                        left: Math.min(rect.left, window.innerWidth - 220) 
                                      }
                                    });
                                  }}
                                  className={`flex md:hidden md:group-hover:flex p-1 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                >
                                  <ChevronDown size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                                </button>
                              )}
                              {!chatSelectMode && unreadCount > 0 && (
                                <span 
                                  data-testid={`unread-count-${chat.id}`}
                                  className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#D4AF37] text-white text-xs font-medium flex items-center justify-center"
                                >
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Button - positioned above bottom nav */}
      {!isEmbedded && (
        <button
          data-testid="new-chat-fab"
          onClick={() => setShowNewChat(true)}
          className="fixed bottom-20 right-5 w-14 h-14 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-gold hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center z-30"
        >
          <Plus size={26} />
        </button>
      )}
      {isEmbedded && (
        <button
          data-testid="new-chat-btn-embedded"
          onClick={() => setShowNewChat(true)}
          className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-gold hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center"
        >
          <Plus size={24} />
        </button>
      )}

      {/* New Chat Modal */}
      <AnimatePresence>
        {showNewChat && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={resetModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`rounded-3xl p-6 w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col ${
                isDarkMode ? 'bg-gray-900' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
              data-testid="new-chat-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {chatMode === 'group' && selectedUsers.length > 0 && (
                    <button
                      onClick={() => setSelectedUsers([])}
                      className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                        isDarkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-text-primary'
                      }`}
                    >
                      <ArrowLeft size={18} />
                    </button>
                  )}
                  <h2 className={`font-heading text-xl ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                    {chatMode === 'direct' ? 'New Chat' : 'New Group'}
                  </h2>
                </div>
                <button
                  data-testid="close-modal-btn"
                  onClick={resetModal}
                  className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                    isDarkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-text-primary'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Mode Toggle Tabs */}
              <div className={`flex gap-1 p-1 rounded-xl mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-[#F5F5F7]'}`}>
                <button
                  data-testid="direct-chat-tab"
                  onClick={() => { setChatMode('direct'); setSelectedUsers([]); }}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    chatMode === 'direct'
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-sm'
                      : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Direct
                </button>
                <button
                  data-testid="group-chat-tab"
                  onClick={() => setChatMode('group')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    chatMode === 'group'
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-sm'
                      : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Group
                </button>
                <button
                  data-testid="broadcast-tab"
                  onClick={() => setChatMode('broadcast')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    chatMode === 'broadcast'
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-sm'
                      : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Broadcast
                </button>
              </div>

              {/* Selected Users for Group/Broadcast */}
              {(chatMode === 'group' || chatMode === 'broadcast') && selectedUsers.length > 0 && (
                <div className="mb-4">
                  <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                    {selectedUsers.length} MEMBER{selectedUsers.length > 1 ? 'S' : ''} SELECTED
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.map(user => (
                      <div
                        key={user.id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                          isDarkMode ? 'bg-gray-800 text-white' : 'bg-[#F5F5F7] text-text-primary'
                        }`}
                      >
                        <span>{user.display_name}</span>
                        <button
                          onClick={() => toggleUserSelection(user)}
                          className="hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Name Input */}
              {(chatMode === 'group' && selectedUsers.length >= 2) || (chatMode === 'broadcast' && selectedUsers.length >= 1) ? (
                <div className="mb-4">
                  <input
                    data-testid="group-name-input"
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    className={`w-full h-12 rounded-xl px-4 text-sm border-2 focus:ring-0 ${
                      isDarkMode 
                        ? 'bg-gray-800 text-white placeholder:text-gray-500 border-gray-700 focus:border-[#D4AF37]' 
                        : 'bg-[#F5F5F7] text-text-primary border-transparent focus:border-[#D4AF37]'
                    }`}
                    placeholder={chatMode === 'broadcast' ? "Enter broadcast list name..." : "Enter group name..."}
                  />
                </div>
              ) : null}

              {/* Search in Modal */}
              <div className="relative mb-4">
                <Search size={18} className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                  isDarkMode ? 'text-gray-500' : 'text-text-tertiary'
                }`} />
                <input
                  data-testid="modal-search-input"
                  type="text"
                  value={modalSearchQuery}
                  onChange={(e) => {
                    setModalSearchQuery(e.target.value);
                    searchUsersInModal(e.target.value);
                  }}
                  className={`w-full h-12 rounded-xl pl-12 pr-4 text-sm border-none focus:ring-2 focus:ring-[#D4AF37] ${
                    isDarkMode 
                      ? 'bg-gray-800 text-white placeholder:text-gray-500' 
                      : 'bg-[#F5F5F7] text-text-primary'
                  }`}
                  placeholder={chatMode === 'broadcast' ? "Search users to add to broadcast..." : chatMode === 'group' ? "Search users to add to group..." : "Search users by name or username..."}
                  autoFocus
                />
              </div>

              {/* Search Results */}
              <div className="flex-1 overflow-y-auto">
                {modalSearchQuery === '' ? (
                  <div className="text-center py-8">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-secondary-DEFAULT'
                    }`}>
                      {chatMode === 'group' || chatMode === 'broadcast' ? (
                        <Users size={28} className={isDarkMode ? 'text-gray-500' : 'text-text-tertiary'} />
                      ) : (
                        <UserPlus size={28} className={isDarkMode ? 'text-gray-500' : 'text-text-tertiary'} />
                      )}
                    </div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                      {chatMode === 'broadcast'
                        ? 'Search users to add to broadcast list'
                        : chatMode === 'group' 
                          ? 'Search users to add to your group' 
                          : 'Search for users to start a chat'}
                    </p>
                  </div>
                ) : modalSearching ? (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>Searching...</div>
                ) : modalSearchResults.length === 0 ? (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>No users found</div>
                ) : (
                  <div className="space-y-2">
                    {modalSearchResults.map((foundUser) => {
                      const isSelected = selectedUsers.some(u => u.id === foundUser.id);
                      
                      return (
                        <button
                          key={foundUser.id}
                          data-testid={`modal-user-${foundUser.id}`}
                          onClick={() => {
                            if (chatMode === 'direct') {
                              createChat(foundUser.id);
                              resetModal();
                            } else {
                              toggleUserSelection(foundUser);
                            }
                          }}
                          className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all duration-200 text-left ${
                            isSelected
                              ? isDarkMode ? 'bg-[#D4AF37]/20 border border-[#D4AF37]' : 'bg-[#D4AF37]/10 border border-[#D4AF37]'
                              : isDarkMode ? 'hover:bg-gray-800/70 active:bg-gray-700' : 'hover:bg-[#F0F0F0] active:bg-[#E5E5E5]'
                          }`}
                        >
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                            {(foundUser.display_name || foundUser.username || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{foundUser.display_name}</h3>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>@{foundUser.username}</p>
                          </div>
                          {(chatMode === 'group' || chatMode === 'broadcast') && (
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                              isSelected 
                                ? 'bg-[#D4AF37] border-[#D4AF37]' 
                                : isDarkMode ? 'border-gray-600' : 'border-gray-300'
                            }`}>
                              {isSelected && <Check size={14} className="text-white" />}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Create Group Button */}
              {chatMode === 'group' && selectedUsers.length >= 2 && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                  <button
                    data-testid="create-group-btn"
                    onClick={createGroupChat}
                    disabled={creatingGroup || !groupName.trim()}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingGroup ? 'Creating...' : `Create Group (${selectedUsers.length} members)`}
                  </button>
                </div>
              )}

              {/* Create Broadcast Button */}
              {chatMode === 'broadcast' && selectedUsers.length >= 1 && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                  <button
                    data-testid="create-broadcast-btn"
                    onClick={createBroadcastList}
                    disabled={creatingGroup || !groupName.trim()}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingGroup ? 'Creating...' : `Create Broadcast (${selectedUsers.length} recipients)`}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Context Menu Portal */}
      {chatContextMenu && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setChatContextMenu(null)} />
          <div
            className={`fixed w-52 rounded-xl shadow-lg border z-[9999] py-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            style={{ top: chatContextMenu.pos.top, left: chatContextMenu.pos.left }}
          >
            {!chatContextMenu.isSystem && (
              <button onClick={(e) => { handleArchiveChat(chatContextMenu.chatId, chatContextMenu.chatData.archived_by?.includes(user?.id), e); setChatContextMenu(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}>
                <Archive size={16} /> {chatContextMenu.chatData.archived_by?.includes(user?.id) ? 'Unarchive chat' : 'Archive chat'}
              </button>
            )}
            <button onClick={async (e) => { e.stopPropagation(); try { const isMuted = chatContextMenu.chatData.muted_by?.includes(user?.id); if (isMuted) { await axios.delete(`${API}/chats/${chatContextMenu.chatId}/mute`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Unmuted'); } else { await axios.post(`${API}/chats/${chatContextMenu.chatId}/mute`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Muted'); } fetchChats(); } catch {} setChatContextMenu(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}>
              <BellOff size={16} /> Mute notifications
            </button>
            <button onClick={(e) => { handlePinChat(chatContextMenu.chatId, chatContextMenu.chatData.is_pinned, e); setChatContextMenu(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}>
              <Pin size={16} /> {chatContextMenu.chatData.is_pinned ? 'Unpin chat' : 'Pin chat'}
            </button>
            {!chatContextMenu.isSystem && (
              <button onClick={(e) => { handleFavoriteChat(chatContextMenu.chatId, chatContextMenu.chatData.is_favorite, e); setChatContextMenu(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}>
                <Heart size={16} /> {chatContextMenu.chatData.is_favorite ? 'Remove favourite' : 'Add to favourites'}
              </button>
            )}
            {!chatContextMenu.isSystem && chatContextMenu.chatData.chat_type === 'direct' && (
              <button onClick={async (e) => { e.stopPropagation(); const otherId = chatContextMenu.chatData.participants?.find(p => p !== user?.id); if (otherId) { try { await axios.post(`${API}/users/${otherId}/block`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('User blocked'); fetchChats(); } catch {} } setChatContextMenu(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                <Ban size={16} /> Block
              </button>
            )}
            {!chatContextMenu.isSystem && (
              <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm('Delete this chat?')) return; try { await axios.delete(`${API}/chats/${chatContextMenu.chatId}/messages`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Chat cleared'); fetchChats(); } catch {} setChatContextMenu(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                <Trash2 size={16} /> Delete chat
              </button>
            )}
          </div>
        </>,
        document.body
      )}

      {/* Stories Modal */}
      {showStoriesModal && <StoriesBar onClose={() => setShowStoriesModal(false)} />}
      {!isEmbedded && <BottomNav />}
    </div>
  );
};

export default ChatList;